import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type {
  Author,
  AuthorListQuery,
  AuthorListResponse,
  CreateAuthorInput,
  UpdateAuthorInput,
} from '@features/authors/types';

/**
 * Backend /api/v1/taxonomy/authors DTO.
 *
 * BE shape differs from FE Author:
 *   - BE has firstName + lastName + (read-only) fullName.
 *     FE Author has a single `displayName`. We split on first whitespace when writing
 *     and reassemble fullName when reading.
 *   - BE: twitterUrl / linkedInUrl / websiteUrl ↔ FE: twitter / linkedin / website
 *   - BE has displayOrder, isFeatured, status. No `articleCount` yet (defaults to 0).
 */
interface AuthorBeDto {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  slug: string | null;
  title: string | null;
  bio: string | null;
  email: string | null;
  avatarUrl: string | null;
  twitterUrl: string | null;
  linkedInUrl: string | null;
  websiteUrl: string | null;
  displayOrder: number;
  isFeatured: boolean;
  status: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateAuthorBeRequest {
  firstName: string;
  lastName?: string | null;
  slug?: string | null;
  title?: string | null;
  bio?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  twitterUrl?: string | null;
  linkedInUrl?: string | null;
  websiteUrl?: string | null;
  displayOrder?: number;
  isFeatured?: boolean;
}

// (BE update payload is structurally identical to CreateAuthorBeRequest.)

const BASE = '/api/v1/taxonomy/authors';

function splitName(displayName: string): { firstName: string; lastName: string | null } {
  const trimmed = displayName.trim();
  const idx = trimmed.indexOf(' ');
  if (idx === -1) return { firstName: trimmed, lastName: null };
  return { firstName: trimmed.slice(0, idx), lastName: trimmed.slice(idx + 1).trim() || null };
}

function mapAuthor(b: AuthorBeDto): Author {
  const displayName =
    b.fullName ?? [b.firstName, b.lastName].filter(Boolean).join(' ').trim() ?? '';
  return {
    id: b.id,
    displayName,
    slug: b.slug ?? '',
    email: b.email ?? undefined,
    title: b.title ?? undefined,
    bio: b.bio ?? undefined,
    avatarUrl: b.avatarUrl ?? undefined,
    twitter: b.twitterUrl ?? undefined,
    linkedin: b.linkedInUrl ?? undefined,
    website: b.websiteUrl ?? undefined,
    articleCount: 0, // TODO: BE will surface this in S10
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

function toBeRequest(input: CreateAuthorInput | UpdateAuthorInput): CreateAuthorBeRequest {
  const dn = (input as CreateAuthorInput).displayName ?? '';
  const { firstName, lastName } = splitName(dn);
  return {
    firstName,
    // BE LastName is optional (mononyms allowed) but the column is non-null —
    // send '' rather than null for single-word display names.
    lastName: lastName ?? '',
    slug: input.slug ?? null,
    title: input.title ?? null,
    bio: input.bio ?? null,
    email: input.email ?? null,
    avatarUrl: input.avatarUrl ?? null,
    twitterUrl: input.twitter ?? null,
    linkedInUrl: input.linkedin ?? null,
    websiteUrl: input.website ?? null,
  };
}

export const authorsApi = {
  async list(query: AuthorListQuery = {}): Promise<AuthorListResponse> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.q) params.search = query.q;
    const res = await http.get<ActionResponse<AuthorBeDto>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapAuthor),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? (query.page ?? 1),
      pageSize: pagination?.pageSize ?? (query.pageSize ?? 20),
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<Author> {
    const dto = unwrap(await http.get<ActionResponse<AuthorBeDto>>(`${BASE}/${id}`));
    return mapAuthor(dto);
  },

  async create(input: CreateAuthorInput): Promise<Author> {
    const newId = unwrap(await http.post<ActionResponse<string>>(BASE, toBeRequest(input)));
    const dto = unwrap(await http.get<ActionResponse<AuthorBeDto>>(`${BASE}/${newId}`));
    return mapAuthor(dto);
  },

  async update(id: string, input: UpdateAuthorInput): Promise<Author> {
    // Pull current to fill in non-provided fields (BE requires full request).
    // For `avatarUrl` we MUST distinguish "caller cleared it" (explicit null) from
    // "caller didn't touch it" (undefined); the previous `?? current` swallowed
    // explicit null and silently re-saved the existing image, so the delete
    // never reached the BE.
    const current = await this.get(id);
    const merged: CreateAuthorInput = {
      displayName: input.displayName ?? current.displayName,
      slug: input.slug ?? current.slug,
      email: input.email ?? current.email,
      title: input.title ?? current.title,
      bio: input.bio ?? current.bio,
      avatarUrl: input.avatarUrl === undefined ? current.avatarUrl : input.avatarUrl,
      twitter: input.twitter ?? current.twitter,
      linkedin: input.linkedin ?? current.linkedin,
      website: input.website ?? current.website,
    };
    await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, toBeRequest(merged));
    const dto = unwrap(await http.get<ActionResponse<AuthorBeDto>>(`${BASE}/${id}`));
    return mapAuthor(dto);
  },

  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<unknown>>(`${BASE}/${id}`);
  },

  async bulkDelete(ids: string[]): Promise<{ deleted: number; skipped: number; skippedIds: string[] }> {
    const dto = unwrap(
      await http.post<
        ActionResponse<{ deleted: number; skipped: number; skippedIds: string[] | null }>
      >(`${BASE}/bulk-delete`, { ids }),
    );
    return { deleted: dto.deleted, skipped: dto.skipped, skippedIds: dto.skippedIds ?? [] };
  },
};
